// InviteShareButton — compact one-click invite-link sharer for staff.
//   Header widget on DispatchDashboard / DriverDashboard.
//
// Click → copies https://<host>/register?ref=<staff-code> + toast confirms
// Long-press / right-click would deep-link to /staff/rewards, but for now we
// keep it as a single click → copy. /staff/rewards has the full QR + history.
//
// Phase 5.2.1+ Reward loop is automatic: when someone signs up via this link,
//   +50 pts → staff_points_ledger immediately
//   +100 pts → staff_points_ledger when their first order delivers
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Link2, Copy, Check } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export function InviteShareButton({ testId = 'invite-share-btn', accent = '#39FF14' }) {
  const [code, setCode] = useState(null);
  const [justCopied, setJustCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await axios.get(`${API_URL}/api/staff/me/qr`, { withCredentials: true });
        if (!cancelled) setCode(data.code);
      } catch {/* non-fatal — button stays disabled */}
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const handleClick = async () => {
    if (!code) return;
    const url = `${window.location.origin}/register?ref=${code}`;
    try {
      // Prefer the modern Web Share sheet if the user is on mobile + the device supports it
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Ash & Merch invite',
            text: `Join me on Ash & Merch — use code ${code}`,
            url,
          });
          return;
        } catch {/* user cancelled — fall through to copy */}
      }
      await navigator.clipboard.writeText(url);
      setJustCopied(true);
      toast.success(`Invite link copied — ${code}`);
      setTimeout(() => setJustCopied(false), 1500);
    } catch {
      toast.error('Could not copy — open /staff/rewards to share manually');
    }
  };

  return (
    <Button
      onClick={handleClick}
      variant="outline"
      disabled={!code}
      data-testid={testId}
      title={code ? `Click to copy your invite link (${code})` : 'Loading...'}
      className="border-[#2A0F3D] hover:border-[#39FF14] rounded-none font-mono"
      style={{ color: justCopied ? '#39FF14' : accent }}
    >
      {justCopied ? <Check className="w-4 h-4 mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
      {justCopied ? 'COPIED' : 'INVITE'}
    </Button>
  );
}

export default InviteShareButton;
